import type { ProspectMatterType } from '@common/enums';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    type SxProps,
    type Theme,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import CurrencyInput from '@/components/CurrencyInput';
import useQuoteIntakeSetupStore, {
    type EstimationDynamicValue,
} from '@/stores/useQuoteIntakeSetupStore';
import { formatCurrency, parseCurrency } from '@/utils';

export default function TitleInsuranceCalculator() {
    const [isTICalculatorOpen, setIsTICalculatorOpen] = useState<boolean>(false);
    const [purchasePrice, setPurchasePrice] = useState<string | number>(0);
    const [selectedPropertyType, setSelectedPropertyType] = useState<EstimationDynamicValue>();
    const [formData] = useQuoteIntakeSetupStore((state) => [state.formData]);

    const titleInsuranceCalculator: SxProps<Theme> = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
    };

    const calculateEstimatedTitleInsurance = () => {
        if (selectedPropertyType) {
            const insurancePercentage = formData.step3.titleInsuranceEstimation.find(
                (value) => value.description === selectedPropertyType.description,
            )?.amount;
            if (!insurancePercentage) {
                return 'N/A';
            }
            const parsedPurchasePrice = parseCurrency(`${purchasePrice}`);
            const estimatedValue = (parsedPurchasePrice * insurancePercentage) / 100;
            return `$${formatCurrency(estimatedValue)}`;
        }
        return 'N/A';
    };

    const getPricingFieldLabel = (matterType: ProspectMatterType) => {
        switch (matterType) {
            case 'Purchase':
                return 'Purchase price';
            case 'Refinance':
                return 'Total loan amount';
            case 'PrivateLender':
                return 'Total loan amount';
            default:
                return 'Price';
        }
    };

    return (
        <Box sx={titleInsuranceCalculator}>
            <Button
                variant="text"
                sx={{ textTransform: 'initial' }}
                endIcon={isTICalculatorOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setIsTICalculatorOpen((prev) => !prev)}
            >
                Show title insurance calculator
            </Button>
            {isTICalculatorOpen ? (
                <>
                    <CurrencyInput
                        label={getPricingFieldLabel(formData.step0.transactionType)}
                        sx={{ width: '100%' }}
                        value={purchasePrice}
                        allowDecimal={true}
                        onChange={(value) => {
                            setPurchasePrice(value);
                        }}
                        helperText={`Enter ${
                            formData.step0.transactionType === 'Purchase'
                                ? 'purchase price'
                                : 'total loan amount'
                        } to calculate title insurance.`}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="property-type-select-label">Property type</InputLabel>
                        <Select
                            labelId="property-type-select-label"
                            value={selectedPropertyType}
                            label="Property type"
                            renderValue={(value) => value?.description}
                            onChange={(e: SelectChangeEvent<any>) =>
                                setSelectedPropertyType(e.target.value)
                            }
                        >
                            {formData.step3.titleInsuranceEstimation.map((value) => {
                                return (
                                    <MenuItem key={value.id ?? value.description} value={value}>
                                        {value.description}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        <FormHelperText>
                            Choose property type to apply title insurance percentage.
                        </FormHelperText>
                    </FormControl>
                    <Divider sx={{ width: '100%' }}></Divider>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <Typography variant="labelMedium">Estimated title insurance</Typography>
                        <Typography variant="h3">{calculateEstimatedTitleInsurance()}</Typography>
                    </Box>
                </>
            ) : null}
        </Box>
    );
}
